import { KeyboardEvent, KeyboardEventHandler, MouseEvent as ReactMouseEvent, MouseEventHandler } from "react";
import { Action } from "redux";

import { bridge, FormOpenMethod, IFormOpenedAction } from "@multiweb/reactCore/bridge";
import { isActivatingKeyEvent, isArray, isLeftButtonEvent, toSerializableKeyEvent, toSerializableMouseEvent } from "@multiweb/reactUtils";
import { IMenuItemData, INotificationGroup, INotificationItem, MenuItemType } from "@multiweb/types/contracts";
import { IFormContext } from "@multiweb/types/multiweb";
import { MouseButton, SerializableMouseEvent, TypedObject } from "@multiweb/types/react";
import { createMenuItemActivatedAction } from "./actions";
import { ScrollContextType } from "./context";
import { logger } from "./logger";
import { NavigationSection } from "./navigation";
import { FlatNotificationGroups, IMenuItemDefaultActionAllowedAction, IMenuPanelItem, MenuItemActivatedType, MenuPanelItems, MenuPanelItemType, NotificationItems } from "./types";

const { logUserInteractionEvent, stopPropagation, getAppConfiguration } = bridge;

/**
 * Transformuje pole notifikačních položek na objekt klíčovaný přes jejich identifikátory. Takto se s daty notifikačních položek lépe pracuje.
 * @param notificationItems Pole notifikačních položek
 */
export const transformNotificationItems = (notificationItems: readonly INotificationItem[]): NotificationItems => (
    notificationItems.slice().reduce((result, notificationItem) => ({
        ...result,
        [notificationItem.id]: notificationItem,
    }), {})
);

/**
 * Transformuje pole notifikačních skupin na plochý objekt klíčovaný přes jejich identifikátory. Takto se s daty notifikačních skupin lépe pracuje.
 * @param notificationGroups Pole notifikačních skupin
 */
export const normalizeNotificationGroups = (notificationGroups: readonly INotificationGroup[]): FlatNotificationGroups => (
    notificationGroups.reduce((result, { unread, type }) => {
        const { name, ...restTypeParams } = type;
        return {
            ...result,
            [name]: {
                unread,
                id: name,
                ...restTypeParams,
            },
        };
    }, {})
);

/**
 * Převede data notifikačních skupin na data položek levého panelu menu.
 * @param notificationGroups Plochá struktura notifikačních skupin
 */
export const notificationGroupsToPanelItems = (notificationGroups: FlatNotificationGroups): MenuPanelItems => (
    Object.values(notificationGroups).reduce((result, { id, titleLocKey, iconCode, unread }) => {
        const panelItemId = createNotificationGroupPanelItemId(id);
        return {
            ...result,
            [panelItemId]: {
                id: panelItemId,
                iconCode,
                locKey: titleLocKey,
                itemId: id,
                highlighted: unread,
                type: MenuPanelItemType.notificationGroup,
                activationType: MenuItemActivatedType.notificationGroup,
            },
        };
    }, {})
);

export type IsOpenInTabEvent = (
    /** Událost vyvolaná myší. */
    event: ReactMouseEvent | SerializableMouseEvent,
) => boolean;

/**
 * Zjistí, zda-li událost odpovídá události pro otevření nové karty prohlížeče.
 * @see IsOpenInTabEvent
 */
export const isOpenInTabEvent: IsOpenInTabEvent = (event) => (
    (event.ctrlKey && event.button === MouseButton.leftButton) || event.button === MouseButton.middleButton
);

/**
 * Získá metodu otevření pro položku formuláře nebo profilu na základě akce, která ji aktivovala.
 * @param action Akce aktivace položky formuláře nebo profilu. Tato funkce již neprovádí kontrolu typu aktivované položky.
 * @return Vrací {@link FormOpenMethod.inTab}, pokud byla položka aktivována myší a zároveň odpovídá specifikaci ve funkci isOpenInTabEvent.
 * Ve všech ostatních případech vrací {@link FormOpenMethod.newOrExisting}.
 */
export const getFormOpenMethodFromActivationAction = (action: IMenuItemDefaultActionAllowedAction): FormOpenMethod => {
    const { mouseEvent } = action.payload;
    return mouseEvent && isOpenInTabEvent(mouseEvent) ? FormOpenMethod.inTab : FormOpenMethod.newOrExisting;
};

/**
 * Určí, zda událost myši vyvolává aktivaci položky menu, která může být otevřena v nové záložce. Narozdíl od běžné položky může být tato položka aktivována prostředním
 * tlačítkem myši.
 * @param event
 * @see isMenuItemActivatedByMouse
 */
export const isOpenableInTabItemActivatedByMouse = (event: ReactMouseEvent) => event.button === MouseButton.leftButton || event.button === MouseButton.middleButton;

/**
 * Určí, zda událost myši vyvolává aktivaci položky menu. Používá se pouze pro položky, které nelze otevřít v nové záložce.
 * @param event
 * @see isOpenableInTabItemActivatedByMouse
 */
export const isMenuItemActivatedByMouse = isLeftButtonEvent;

/**
 * Určí, zda událost klávesnice vyvolává aktivaci položky menu.
 * @param event
 */
export const isMenuItemActivatedByKey = isActivatingKeyEvent;

/**
 * Vytvoří obalující handler pro událost myši, který zajistí zastavení propagace události, pokud se jedná o aktivační událost. Zabraňuje aktivaci
 * rodičovských položek, kterou by jinak propagace aktivační události způsobila.
 * Pokud dojde k potlačení události, informuje aplikaci interakci uživatele, na základě které aplikaci může prodloužit vypršení session.
 *
 * Zabraňuje také výchozímu chování, z důvodu popsaného u {@link activationMouseBlockHandler}.
 *
 * @param mouseHandler Handler, který je obalen. Volá se vždy, nezávisle na typu události.
 */
export const createActivationMouseHandler = (mouseHandler?: MouseEventHandler) => (event: ReactMouseEvent) => {
    if (isOpenableInTabItemActivatedByMouse(event) || isMenuItemActivatedByMouse(event)) {
        logUserInteractionEvent(event.nativeEvent);
        stopPropagation(event);
        event.preventDefault();
    }
    if (mouseHandler) {
        mouseHandler(event);
    }
};

/**
 * Vytvoří obalující handler pro událost stisknutí klávesy, který zajistí zastavení propagace události, pokud se jedná o aktivační událost. Zabraňuje aktivaci
 * rodičovských položek, kterou by jinak propagace aktivační události způsobila.
 * Pokud dojde k potlačení události, informuje aplikaci interakci uživatele, na základě které aplikaci může prodloužit vypršení session.
 *
 * Zabraňuje také výchozímu chování, z důvodu popsaného u {@link activationKeyBlockHandler}.
 *
 * @param keyHandler Handler, který je obalen. Volá se vždy, nezávisle na typu události.
 */
export const createActivationKeyHandler = (keyHandler?: KeyboardEventHandler) => (event: KeyboardEvent) => {
    if (isMenuItemActivatedByKey(event)) {
        logUserInteractionEvent(event.nativeEvent);
        event.stopPropagation();
        event.preventDefault();
    }
    if (keyHandler) {
        keyHandler(event);
    }
};

/**
 * Pomocný handler pro událost stisknutí klávesy. Zabraňuje výchozímu chování a propagaci události při stisku aktivačních kláves.
 *
 * Používá se v onKeyDown jako doplněk k onKeyUp. I tam je nutné zabránit propagaci a výchozímu chování, jinak stisknutí mezerníku způsobuje kromě aktivace také
 * nechtěné posunutí scrollbaru v nadřazeném scrollovatelném elementu.
 */
export const activationKeyBlockHandler = createActivationKeyHandler();

/**
 * Pomocný handler pro událost tlačítek myši. Zabraňuje výchozímu chování a propagaci události při stisku aktivačních tlačítek.
 *
 * Používá se v onMouseDown jako doplněk k onMouseUp. I tam je nutné zabránit propagaci a výchozímu chování, jinak stisknutí prostředního tlačítka způsobuje kromě
 * aktivace také přechod do scroll režimu (v chrome v menu notifikací).
 */
export const activationMouseBlockHandler = createActivationMouseHandler();

/**
 * Vytvoří obslužnou funkci pro vyvolání obecné akce aktivace položky menu, kterou lze otvírat v nové záložce. Tento typ položek může mít odlišné aktivační podmínky.
 * @param id Identifikátor položky unikátní v rámci typu.
 * @param type Typ položky.
 * @param section Sekce, ve které je položka vykreslená.
 * @param menuItemActivated Dispatch funkce pro MenuItemActivated akci.
 * @see createMenuItemMouseActivationHandler
 */
export const createOpenableInTabItemMouseActivationHandler = (id: number | string, type: MenuItemActivatedType, section: NavigationSection, menuItemActivated: typeof createMenuItemActivatedAction) => (mouseEvent: ReactMouseEvent) => {
    if (isOpenableInTabItemActivatedByMouse(mouseEvent)) {
        menuItemActivated(id, type, section, toSerializableMouseEvent(mouseEvent));
    }
};

/**
 * Vytvoří obslužnou funkci pro vyvolání obecné akce aktivace položky menu při aktivaci myší.
 * @param id Identifikátor položky unikátní v rámci typu.
 * @param type Typ položky.
 * @param section Sekce, ve které je položka vykreslená.
 * @param menuItemActivated Dispatch funkce pro MenuItemActivated akci.
 * @see createOpenableInTabItemMouseActivationHandler
 */
export const createMenuItemMouseActivationHandler = (id: number | string, type: MenuItemActivatedType, section: NavigationSection, menuItemActivated: typeof createMenuItemActivatedAction, profileId?: number) => (mouseEvent: ReactMouseEvent) => {
    if (isMenuItemActivatedByMouse(mouseEvent)) {
        menuItemActivated(id, type, section, toSerializableMouseEvent(mouseEvent), undefined, undefined, profileId);
    }
};

/**
 * Vytvoří obslužnou funkci pro vyvolání obecné akce aktivace položky menu při stisku aktivační klávesy.
 * @param id Identifikátor položky unikátní v rámci typu.
 * @param type Typ položky.
 * @param section Sekce, ve které je položka vykreslená.
 * @param menuItemActivated Dispatch funkce pro MenuItemActivated akci.
 */
export const createMenuItemKeyActivationHandler = (id: number | string, type: MenuItemActivatedType, section: NavigationSection, menuItemActivated: typeof createMenuItemActivatedAction) => (keyboardEvent: KeyboardEvent) => {
    if (isMenuItemActivatedByKey(keyboardEvent)) {
        menuItemActivated(id, type, section, undefined, toSerializableKeyEvent(keyboardEvent));
    }
};

/**
 * Vytvoří funkci obalující scroll funkci z OverlayScrollbars, která přijímá element, na který se scrolluje
 * s nastaveným vycentrováním. Centrování scrollu je nutné kvůli kompatibilitě s absolutně pozicovanou hlavičkou/patičkou.
 * @param scroll Funkce scroll z OverlayScrollbars obalující element.
 * @param margin Odsazení použité při scrollování.
 */
export const createMenuCustomScroll = <T extends HTMLElement>(scroll: ScrollContextType, margin: number) => (element: T) => {
    if (scroll) {
        // Scroll se nemůže volat hned, jinak správně nefunguje scroll přes začátek ve virtualizovaném listu. Hodnota 100ms je určena pomocí experimentování.
        setTimeout(() => {
            scroll({
                el: element,
                scroll: "ifneeded",
                block: "nearest",
                margin: [margin, 0],
            });
        }, 100);
    } else {
        logger.warn("Scroll function is not defined. Cannot scroll to element.");
    }
};

/**
 * Vytvoří identifikátor formulářové položky panelu menu.
 * @param formItemId Identifikátor formulářové položky
 */
export const createFormPanelItemId = (formItemId: number) => addPrefix(formItemId, MenuPanelItemType.form);

/**
 * Vytvoří identifikátor položky panelu menu reprezentující notifikační skupinu.
 * @param notificationGroupId Identifikátor notifikační skupiny
 */
export const createNotificationGroupPanelItemId = (notificationGroupId: string) => (
    addPrefix(notificationGroupId, MenuPanelItemType.notificationGroup)
);

/**
 * Vrací hodnotu rozšířenou o zadaný prefix.
 * @param value Rozšiřovaná hodnota
 * @param prefix Přidávaný prefix
 */
export const addPrefix = (value: string | number, prefix: string) => `${prefix}${value}`;

/**
 * Zjistí, zda položka panelu menu reprezentuje formulářovou položku.
 * @param panelItem Položka panelu menu
 */
export const isFormPanelItem = (panelItem: IMenuPanelItem) => panelItem.type === MenuPanelItemType.form;

/**
 * Zjistí, zda položka panelu menu reprezentuje notifikační skupinu.
 * @param panelItem Položka panelu menu
 */
export const isNotificationGroupPanelItem = (panelItem: IMenuPanelItem) => panelItem.type === MenuPanelItemType.notificationGroup;

/**
 * Transformuje data položek menu na položky panelu menu.
 * @param menuItems Položky menu
 */
export const menuItemsToPanelItems = (menuItems: TypedObject<IMenuItemData>): MenuPanelItems => (
    Object.values(menuItems)
        .map(({ id, icon, locKey, type }) => ({
            id: createFormPanelItemId(id),
            iconCode: icon,
            locKey,
            itemId: id,
            highlighted: false,
            type: MenuItemType.form,
            activationType: menuItemTypeToActivationType(type),
        }))
        .reduce((result, item) => ({
            ...result,
            [item.id]: item,
        }), {})
);

/**
 * Převádí {@link MenuItemType} na {@link MenuItemActivatedType}.
 * @param menuItemType Typ pro převedení.
 */
const menuItemTypeToActivationType = (menuItemType: MenuItemType): MenuItemActivatedType => {
    switch (menuItemType) {
        case MenuItemType.form: return MenuItemActivatedType.panelItemForm;
        case MenuItemType.folder: return MenuItemActivatedType.panelItemFormFolder;
        case MenuItemType.combined: return MenuItemActivatedType.panelItemCombinedItemForm;
        default: throw new Error(`[MainMenu] Unrecognized menu item type: ${menuItemType}. Cannot create menu panel item.`);
    }
};

/**
 * Převede akci aktivace položky na id sekce. Pokud aktivovaná položka není z menu panelu a nereprezentuje sekci, vrací undefined.
 * @param action Akce aktivace pro převedení.
 * @return Id sekce nebo undefined, pokud byla aktivována položka bez sekce.
 */
export const activationActionToMenuPanelItemId = (action: IMenuItemDefaultActionAllowedAction): string | undefined => {
    const { itemId, itemType } = action.payload;
    switch (itemType) {
        case MenuItemActivatedType.staticPanelItem:
            return itemId as string;
        case MenuItemActivatedType.panelItemForm:
        case MenuItemActivatedType.panelItemFormFolder:
        case MenuItemActivatedType.panelItemCombinedItemForm:
        case MenuItemActivatedType.panelItemCombinedItemFolder:
            return createFormPanelItemId(itemId as number);
        case MenuItemActivatedType.notificationGroup:
            return createNotificationGroupPanelItemId(itemId as string);
        default:
            return undefined;
    }
};

/**
 * Vytvoří vzor pro rozpoznávání akcí povolujících výchozí chování pro zadané typy položek.
 * @param actionTypes Hledaný typ položek. Může být konkrétní typ nebo pole odpovídajících typů.
 */
export const createAllowedDefaultActionPattern = (actionTypes: MenuItemActivatedType | readonly MenuItemActivatedType[]) => (action: Action): boolean => {
    if (action instanceof IMenuItemDefaultActionAllowedAction) {
        const { itemType } = action.payload;
        if (isArray(actionTypes)) {
            return actionTypes.includes(itemType);
        } else {
            return itemType === actionTypes;
        }
    } else {
        return false;
    }
};
/**
 * Vytvoří identifikátor pro položku profilu. Jeden profil může být vykreslený vícekrát u různých položek formuláře. V takových případech
 * nestačí k jednoznačné identifikaci profileId. Řeší se přidáním id rodičovské položky formuláře.
 * @param profileId Id profilu.
 * @param parentFormItemId Id položky formuláře, pod kterým je profil vykreslený.
 */
export const createItemIdForProfile = (profileId: number, parentFormItemId?: number): string => {
    if (parentFormItemId) {
        return `${profileId}-${parentFormItemId}`;
    } else {
        return `${profileId}`;
    }
};

/**
 * Zjistí, zda zadaná akce je akcí aktivující formulářovou část kombinované položky menu panelu.
 * @param action Akce ke zkontrolování.
 */
export const isCombinedItemFormPartActivatedAction = (action: Action): boolean => action instanceof IMenuItemDefaultActionAllowedAction &&
    action.payload.itemType === MenuItemActivatedType.panelItemCombinedItemForm;

/**
 * V konfiguraci zjisti, jaký má být počet v minulosti otevřených formulářů přečtením parametru `openedFormsHistoryCount`.
 * Pokud je číslo uložené v parametru vyšší než nula, vrací `true`, v opačném případě `false`.
 */
export const hasOpenedFormsHistoryCountSet = (): boolean => {
    return getAppConfiguration().openedFormsHistoryCount > 0;
};

/**
 * Vytvoří kontext formuláře z položky menu
 * @param formItem Položka menu
 */
export const createFormContext = (formItem: IMenuItemData): Promise<IFormContext | undefined> => {
    if (formItem?.roles?.length) {
        return Promise.resolve({
            roles: formItem.roles,
        });
    } else {
        return Promise.resolve(undefined);
    }
};

/**
 * Vzor pro rozpoznání akce {@link IFormOpenedAction} s parametrem showInMenu nastaveným na true. Umožňuje odfiltrovat akce otevření formuláře, které nemají vliv na hlavní menu.
 * @param action Akce k ověření.
 */
export const formOpenedAndShowInMenuActionPattern = (action: Action): boolean => action instanceof IFormOpenedAction && !!action.payload.openedFormInfo.showInMenu;
